import { useQuery } from "@apollo/client/react";
import type { Role } from "@athena/common/graphql/generated/schema";
import { CurrentUserDocument } from "@/graphql/auth.generated";
import { env } from "@/lib/env";

/**
 * Organizations piloting the alumni coordinator console.
 *
 * Matched by id rather than by the org's name, so a rename cannot silently switch the console off
 * and an unrelated org given the same name cannot switch it on. The id differs per environment and
 * is baked into the build (`VITE_ALUMNI_SUB_ORG_ID`); unset, nobody is in the pilot.
 */
export const ADMIN_PILOT_ORGANIZATION_IDS: ReadonlySet<string> = new Set<string>(
    env.alumniSubOrgId ? [env.alumniSubOrgId] : [],
);

/** Where the alumni console lives. Coordinators are kept inside it; platform ADMINs in the pilot may visit. */
export const ALUMNI_ROOT = "/alumni";

/** Whether `pathname` is inside the console — by whole segment, so `/alumni-archive` is not. */
export const isAlumniPath = (pathname: string): boolean =>
    pathname === ALUMNI_ROOT || pathname.startsWith(`${ALUMNI_ROOT}/`);

/** The coordinator's outreach queue, inside the console. */
export const CLIENT_QUEUE = `${ALUMNI_ROOT}/queue`;

export const CONTACT_LOG = `${ALUMNI_ROOT}/log`;

/** Adding clients from a CSV, reached from the directory. */
export const CLIENT_IMPORT = `${ALUMNI_ROOT}/import`;

/** The coordinator's dashboard: for now, birthdays and sober anniversaries by month. */
export const DASHBOARD = `${ALUMNI_ROOT}/dashboard`;

export const CHAT = `${ALUMNI_ROOT}/chat`;

/**
 * The coordinator's own account page — the console's copy of the portal's `/profile`, which the gate
 * would otherwise bounce them out of. Not an alum's profile; those are `/alumni/:clientId`.
 */
export const MY_PROFILE = `${ALUMNI_ROOT}/profile`;

/** One email to every alum at once. */
export const BROADCAST = `${ALUMNI_ROOT}/broadcast`;

/** Push notifications to alumni, one or many at a time. */
export const PUSH_NOTIFICATIONS = `${ALUMNI_ROOT}/push-notifications`;

/**
 * Who gets the console: ORG_ADMIN and above. Alumni coordinators are ORG_ADMINs of the pilot org;
 * platform ADMIN and SYSTEM in it support them.
 */
const PILOT_ROLES: ReadonlySet<Role> = new Set<Role>([
    "ORG_ADMIN",
    "ADMIN",
    "SYSTEM",
]);

interface PilotCandidate {
    role: Role;
    memberships: { organization: { id: string } }[];
}

/**
 * An ORG_ADMIN or above who is also a member of a pilot organization sees the alumni console
 * instead of the admin portal.
 *
 * Presentation only, like the sidebar's role lists: this decides which shell to draw, never what
 * is allowed. Whatever the console eventually reads is gated server-side.
 */
export const isAdminPilotUser = (
    user: PilotCandidate | null | undefined,
    pilotOrganizationIds: ReadonlySet<string> = ADMIN_PILOT_ORGANIZATION_IDS,
): boolean =>
    !!user &&
    PILOT_ROLES.has(user.role) &&
    user.memberships.some((membership) => pilotOrganizationIds.has(membership.organization.id));

/** Pilot users who may also use the admin portal: platform staff, not the coordinators they support. */
const SHELL_SWITCHER_ROLES: ReadonlySet<Role> = new Set<Role>([
    "ADMIN",
    "SYSTEM",
]);

/**
 * Which shells a user may be in. `alumni` keeps them inside the console, `portal` outside it, and
 * `both` lets them switch — a platform ADMIN in the pilot org, who lands in the portal and opens
 * the console from the sidebar.
 */
export type ShellAccess = "alumni" | "portal" | "both";

export const shellAccessFor = (
    user: PilotCandidate | null | undefined,
    pilotOrganizationIds: ReadonlySet<string> = ADMIN_PILOT_ORGANIZATION_IDS,
): ShellAccess => {
    if (!user || !isAdminPilotUser(user, pilotOrganizationIds)) return "portal";
    return SHELL_SWITCHER_ROLES.has(user.role) ? "both" : "alumni";
};

export const useShellAccess = (): ShellAccess => {
    const { data } = useQuery(CurrentUserDocument);
    return shellAccessFor(data?.currentUser);
};
